/**
 * Utility functions to store and retrieve custom interview creation parameters
 * Used when user comes from extension but isn't logged in
 */

import { CustomInterviewFormData } from '../components/interviews/CustomInterviewModal';

const STORAGE_KEY = 'pending_custom_interview';

/**
 * Stores custom interview parameters in localStorage
 * @param params - Partial custom interview form data from URL parameters
 */
export const storePendingCustomInterview = (params: Partial<CustomInterviewFormData>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    console.log('✅ Stored pending custom interview params:', params);
  } catch (error) {
    console.error('❌ Failed to store pending custom interview params:', error);
  }
};

/**
 * Retrieves stored custom interview parameters from localStorage
 * @returns Partial custom interview form data or null if not found
 */
export const getPendingCustomInterview = (): Partial<CustomInterviewFormData> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const params = JSON.parse(stored);
      console.log('✅ Retrieved pending custom interview params:', params);
      return params;
    }
    return null;
  } catch (error) {
    console.error('❌ Failed to retrieve pending custom interview params:', error);
    return null;
  }
};

/**
 * Clears stored custom interview parameters from localStorage
 */
export const clearPendingCustomInterview = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('✅ Cleared pending custom interview params');
  } catch (error) {
    console.error('❌ Failed to clear pending custom interview params:', error);
  }
};

/**
 * Checks if there are pending custom interview parameters
 * @returns true if parameters exist, false otherwise
 */
export const hasPendingCustomInterview = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch (error) {
    console.error('❌ Failed to check pending custom interview params:', error);
    return false;
  }
};

/**
 * Parses custom interview parameters from URL search params
 * @param searchParams - URLSearchParams object
 * @returns Partial custom interview form data
 */
export const parseCustomInterviewParams = (searchParams: URLSearchParams): Partial<CustomInterviewFormData> | null => {
  const createInterview = searchParams.get('create-interview');
  
  if (createInterview !== 'true') {
    return null;
  }
  
  // Helper function to safely decode URI component
  const safeDecodeURIComponent = (str: string | null): string => {
    if (!str) return '';
    try {
      return decodeURIComponent(str);
    } catch (error) {
      console.warn('Failed to decode URI component:', str, error);
      return str;
    }
  };

  const params: Partial<CustomInterviewFormData> = {
    jobRole: searchParams.get('jobRole') || '',
    companyName: searchParams.get('company') || '',
    interviewRoundType: searchParams.get('roundType') || '',
    jobDescription: safeDecodeURIComponent(searchParams.get('jobDescription')),
    interviewLanguage: searchParams.get('language') || 'English'
  };
  
  // Only return if at least one param is present
  const hasAnyParam = Object.values(params).some(value => value && value !== '');
  return hasAnyParam ? params : null;
};
